import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

const CORE_API = import.meta.env.VITE_CORE_API_URL || ''

interface Meter {
  kind: string
  current_cents: number
  target_cents: number
  pct: number
  cycles_completed: number
  lifetime_cents: number
}

interface CeremonyResult {
  id: string
  kind: string
  slots: number
  winners: number
  executed_at: string
  sbn_block_id: string | null
}

function formatCurrency(cents: number) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(cents / 100)
}

function formatDate(d: string) {
  try {
    return new Date(d).toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })
  } catch { return d }
}

function kindLabel(k: string) {
  if (k === 'classroom') return 'Hey, Educator!'
  if (k === 'lockerroom') return 'Locker Rooms'
  if (k === 'smb') return 'Small Business Fund'
  if (k === 'rental') return 'Rental Assistance'
  return k
}

function kindColor(k: string) {
  if (k === 'classroom') return { bg: 'bg-amber-50', border: 'border-amber-200', accent: '#B45309', chip: 'bg-amber-100 text-amber-800' }
  if (k === 'lockerroom') return { bg: 'bg-indigo-50', border: 'border-indigo-200', accent: '#4338CA', chip: 'bg-indigo-100 text-indigo-800' }
  if (k === 'smb') return { bg: 'bg-emerald-50', border: 'border-emerald-200', accent: '#047857', chip: 'bg-emerald-100 text-emerald-800' }
  if (k === 'rental') return { bg: 'bg-sky-50', border: 'border-sky-200', accent: '#0369A1', chip: 'bg-sky-100 text-sky-800' }
  return { bg: 'bg-stone-50', border: 'border-stone-200', accent: '#57534E', chip: 'bg-stone-100 text-stone-800' }
}

export default function Ceremonies() {
  const [meters, setMeters] = useState<Meter[]>([])
  const [ceremonies, setCeremonies] = useState<CeremonyResult[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    Promise.all([
      fetch(`${CORE_API}/api/v1/draw/meters`).then((r) => r.ok ? r.json() : null).catch(() => null),
      fetch(`${CORE_API}/api/v1/draw/ceremonies/public`).then((r) => r.ok ? r.json() : null).catch(() => null),
    ]).then(([metersRes, cerRes]) => {
      setMeters(metersRes?.data?.meters || [])
      setCeremonies(cerRes?.data?.ceremonies || [])
    }).finally(() => setLoading(false))
  }, [])

  return (
    <div className="min-h-screen bg-gradient-to-br from-shell via-white to-shell/50">
      <div className="mx-auto max-w-5xl px-4 py-10">
        <header className="mb-8 text-center">
          <p className="text-xs font-bold uppercase tracking-widest text-cocoa/50">Transparency</p>
          <h1 className="mt-1 text-3xl font-bold text-cocoa md:text-4xl">Draw Ceremonies</h1>
          <p className="mt-2 text-sm text-cocoa/60 md:text-base">
            Every ceremony is a weighted random draw. Funding meters track progress. SBN block IDs provide cryptographic proof that each draw was fair.
          </p>
        </header>

        {loading ? (
          <div className="flex justify-center py-16">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-cocoa border-t-transparent" />
          </div>
        ) : (
          <div className="space-y-10">
            {/* Funding meters */}
            {meters.length > 0 && (
              <section>
                <h2 className="mb-4 text-xl font-bold text-cocoa">Funding Progress</h2>
                <div className="grid gap-3 md:grid-cols-2">
                  {meters.map((m) => {
                    const c = kindColor(m.kind)
                    return (
                      <div key={m.kind} className={`rounded-2xl border ${c.border} ${c.bg} p-5`}>
                        <div className="flex items-center justify-between">
                          <h3 className="font-bold text-cocoa">{kindLabel(m.kind)}</h3>
                          <span className="text-xs text-cocoa/50">{m.cycles_completed} cycle{m.cycles_completed !== 1 ? 's' : ''} completed</span>
                        </div>
                        <div className="mt-3 h-3 overflow-hidden rounded-full bg-white/70">
                          <div
                            className="h-full rounded-full transition-all"
                            style={{ width: `${Math.min(m.pct, 100)}%`, backgroundColor: c.accent }}
                          />
                        </div>
                        <div className="mt-2 flex items-baseline justify-between text-sm">
                          <span className="font-semibold text-cocoa">{formatCurrency(m.current_cents)} / {formatCurrency(m.target_cents)}</span>
                          <span className="text-xs text-cocoa/60">{Math.round(m.pct)}%</span>
                        </div>
                        {m.lifetime_cents > m.current_cents && (
                          <p className="mt-1 text-[10px] text-cocoa/40">Lifetime: {formatCurrency(m.lifetime_cents)}</p>
                        )}
                      </div>
                    )
                  })}
                </div>
              </section>
            )}

            {/* Past ceremonies */}
            <section>
              <h2 className="mb-4 text-xl font-bold text-cocoa">Past Ceremonies</h2>
              {ceremonies.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-sand bg-white/70 p-10 text-center">
                  <p className="text-sm text-cocoa/60">No ceremonies have happened yet.</p>
                  <p className="mt-1 text-xs text-cocoa/40">Keep watching — the first draw will appear here when it runs.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {ceremonies.map((c) => {
                    const colors = kindColor(c.kind)
                    return (
                      <div key={c.id} className="rounded-2xl border border-sand/50 bg-white/80 p-5 shadow-sm">
                        <div className="flex flex-wrap items-start justify-between gap-3">
                          <div>
                            <div className="flex items-center gap-2">
                              <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase ${colors.chip}`}>
                                {kindLabel(c.kind)}
                              </span>
                              <span className="text-xs text-cocoa/60">{formatDate(c.executed_at)}</span>
                            </div>
                            <p className="mt-2 text-base font-semibold text-cocoa">
                              {c.winners} winner{c.winners !== 1 ? 's' : ''} selected from {c.slots} slot{c.slots !== 1 ? 's' : ''}
                            </p>
                          </div>
                          {c.sbn_block_id && (
                            <div className="rounded-lg bg-stone-100 px-3 py-2 font-mono text-[10px] text-stone-600">
                              <p className="text-[9px] uppercase tracking-wider text-stone-400">SBN Proof</p>
                              <p className="mt-0.5">{c.sbn_block_id.slice(0, 16)}...</p>
                            </div>
                          )}
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </section>

            {/* CTA */}
            <section className="rounded-2xl bg-gradient-to-br from-cocoa to-cocoa/80 p-6 text-center text-white">
              <h2 className="text-xl font-bold">Want to participate?</h2>
              <p className="mt-1 text-sm text-white/80">
                Nominate a teacher or prospect, or support someone already in the pool.
              </p>
              <div className="mt-4 flex flex-wrap justify-center gap-2">
                <Link to="/nominate" className="rounded-xl bg-white px-5 py-2 text-sm font-bold text-cocoa transition hover:bg-white/90">
                  Nominate
                </Link>
                <Link to="/portal/nominee" className="rounded-xl border border-white/40 px-5 py-2 text-sm font-semibold text-white transition hover:bg-white/10">
                  The Pool
                </Link>
                <Link to="/rally" className="rounded-xl border border-white/40 px-5 py-2 text-sm font-semibold text-white transition hover:bg-white/10">
                  Rally Board
                </Link>
              </div>
            </section>
          </div>
        )}
      </div>
    </div>
  )
}
